import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Edit, Plus, Search, ShieldOff, Trash2, Unlock } from "lucide-react";
import { ativarUsuario, desativarUsuario, excluirUsuario, listarUsuarios } from "../services/api";

const UsuariosRevendaDistribuidor = ({ token }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const revendaId = searchParams.get("revendaId") || "";
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await listarUsuarios(token, revendaId ? { revendaId: Number(revendaId) } : undefined);
      setItens(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, revendaId]);

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return itens;
    return itens.filter((u) => [u.nome, u.email].some((v) => String(v || "").toLowerCase().includes(termo)));
  }, [itens, busca]);

  const query = revendaId ? `?revendaId=${revendaId}` : "";

  const alternar = async (u) => {
    if (!window.confirm(u.ativo ? `Desativar ${u.nome}?` : `Ativar ${u.nome}?`)) return;
    try {
      if (u.ativo) await desativarUsuario(u.id, token);
      else await ativarUsuario(u.id, token);
      await carregar();
    } catch (e) {
      alert(e?.response?.data || "Erro ao alterar o usuário.");
    }
  };

  const excluir = async (u) => {
    if (!window.confirm(`Excluir ${u.nome}?`)) return;
    try {
      await excluirUsuario(u.id, token);
      await carregar();
    } catch (e) {
      alert(typeof e?.response?.data === "string" ? e.response.data : "Erro ao excluir o usuário.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Usuários da Revenda</h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">Acessos das revendas deste distribuidor.</p>
          </div>
          <button type="button" onClick={() => navigate(`/revendas/usuarios/novo${query}`)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm">
            <Plus size={16} /> Novo usuário
          </button>
        </div>
        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome ou e-mail" className="w-full pl-9 pr-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left p-3">Nome</th>
                <th className="text-left p-3">E-mail</th>
                <th className="text-left p-3">Perfil</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="p-6 text-center text-gray-500">Carregando...</td></tr>}
              {!loading && filtrados.map((u) => (
                <tr key={u.id} className="border-t dark:border-white/10">
                  <td className="p-3">{u.nome}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">{u.perfilNome || u.perfilCodigo || "—"}</td>
                  <td className="p-3">{u.ativo ? "Ativo" : "Inativo"}</td>
                  <td className="p-3 text-right space-x-2">
                    <button type="button" className="inline-flex items-center gap-1 text-blue-600" onClick={() => navigate(`/revendas/usuarios/editar/${u.id}${query}`)}>
                      <Edit size={14} /> Editar
                    </button>
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => alternar(u)}>
                      {u.ativo ? <ShieldOff size={14} /> : <Unlock size={14} />}
                      {u.ativo ? "Desativar" : "Ativar"}
                    </button>
                    <button type="button" className="inline-flex items-center gap-1 text-red-600" onClick={() => excluir(u)}>
                      <Trash2 size={14} /> Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !filtrados.length && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Nenhum usuário encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsuariosRevendaDistribuidor;
